import _                                          from 'underscore';
import { useContext }                             from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router';

import { useCurrentUser }                                                                                 from './useAccount';
import { CorvalWorkflowActionType, PlanningAreaParamsSection, RouterLocationParams, RouterLocationState } from '../types';
import { RouterUtility }                                                                                  from '../util';
import { ProjectContext }                                                                                 from '../views/pages/projects/ProjectIndexPage';
import { useInitialCategory }                                                                             from './useInitialCategory';
import { useFeatureFlags }                                                                                from './useFeatureFlags';


interface WorkflowActionTypeMapValue {
    promptLabel: string,
    callback: Function,
}

type UseWorkflowActionsHookMethod = () => UseWorkflowActionsHook;

export type UseWorkflowActionsHook = {
    triggerWorkflowAction: ( type: CorvalWorkflowActionType ) => void,
    getTriggerActionPrompt: ( type: CorvalWorkflowActionType ) => string,
    triggerTypeAllowed: ( type: CorvalWorkflowActionType ) => boolean,
};

export const useWorkflowActions: UseWorkflowActionsHookMethod = () => {

    let history                        = useHistory<RouterLocationState>(),
        location                       = useLocation<RouterLocationState>(),
        match                          = useRouteMatch<RouterLocationParams.PlanningArea>(),
        { currentUser }                = useCurrentUser(),
        { projectId } = useContext( ProjectContext ),
        { firstCategoryId }            = useInitialCategory(),
        featureFlags                   = useFeatureFlags();

    const getTriggerInfo = ( type, attribute: keyof WorkflowActionTypeMapValue = 'callback' ): Function | string => {

        // No Type / Attribute
        if( !type || !attribute ) return null;

        // No Action Required
        if( type === CorvalWorkflowActionType.None ) return null;

        // Action Mapping
        let actionMap = new Map<CorvalWorkflowActionType, WorkflowActionTypeMapValue>( [
            [
                CorvalWorkflowActionType.Reviewinitialinquiry, {
                promptLabel: 'Review Inquiry',
                callback:    reviewInitialInquiry,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewtimeline, {
                promptLabel: 'Review Milestones',
                callback:    reviewTimeline,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewstrategicinputs, {
                promptLabel: 'Review Assumptions',
                callback:    reviewStrategicInputs,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewobjectives, {
                promptLabel: 'Review Objectives',
                callback:    reviewObjectives,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewactivitymap, {
                promptLabel: 'Open Map',
                callback:    reviewActivityMap,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewroadmap, {
                promptLabel: 'Open Roadmap',
                callback:    reviewRoadmap,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewbudgetsummary, {
                promptLabel: 'View Budget',
                callback:    reviewBudgetSummary,
            }
            ],
            [
                CorvalWorkflowActionType.Reviewresourcesummary, {
                promptLabel: 'View Resourcing',
                callback:    reviewResourceSummary,
            }
            ],
        ] );

        if( actionMap.has( type ) && actionMap.get( type )[ attribute ] ){

            return actionMap.get( type )[ attribute ];

        } else {

            throw new Error( `No action type "${ type }" or attribute "${ attribute } exists on mapping.` )
        }
    };

    const triggerTypeAllowed: UseWorkflowActionsHook['triggerTypeAllowed'] = ( type ) => {

        switch( type ){
            case CorvalWorkflowActionType.Reviewresourcesummary:
            case CorvalWorkflowActionType.Reviewbudgetsummary:

                return ( featureFlags.permissionProjectResourcePlanningView );

                break;

            case CorvalWorkflowActionType.Reviewinitialinquiry:
            case CorvalWorkflowActionType.Reviewobjectives:
            case CorvalWorkflowActionType.Reviewstrategicinputs:
            case CorvalWorkflowActionType.Reviewtimeline:
                return false;

                break;
        }

        return true;
    }

    const triggerWorkflowAction: UseWorkflowActionsHook['triggerWorkflowAction'] = ( type, ...args ) => {

        let callback = getTriggerInfo( type, 'callback' );

        if( _.isFunction( callback ) ){

            callback.apply( this, ...args ) as Function;
        }
    };

    const getTriggerActionPrompt: UseWorkflowActionsHook['getTriggerActionPrompt'] = ( type ) => {

        return getTriggerInfo( type, 'promptLabel' ) as string;
    };

    const pushRoute = ( routePath ) => {

        history.push( routePath, { referrer: location, from: location.pathname } );
    }

    const reviewInitialInquiry = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.SURVEYS.INQUIRY', { projectId } );

        pushRoute( routePath );
    }

    const reviewTimeline = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId, section: PlanningAreaParamsSection.PROJECT_KEY_MILESTONES } );

        pushRoute( routePath );
    }

    const reviewStrategicInputs = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.PLANNING', { projectId, section: PlanningAreaParamsSection.QUESTIONS_BY_CATEGORY } );

        pushRoute( routePath );
    }

    const reviewObjectives = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.OBJECTIVES.ROADMAP', { projectId, categoryId: firstCategoryId } );

        pushRoute( routePath );
    }

    const reviewActivityMap = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.MAP', { projectId } );

        pushRoute( routePath );
    }

    const reviewRoadmap = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.OBJECTIVES.ROADMAP', { projectId } );

        pushRoute( routePath );
    }

    const reviewBudgetSummary = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.RESOURCE_PLANNING.BUDGET_BY_DASHBOARD_DATA', { projectId } );

        pushRoute( routePath );
    }

    const reviewResourceSummary = () => {

        let routePath = RouterUtility.getRoute( 'PROJECT.RESOURCE_PLANNING.BUDGET_HIRING_DATA', { projectId } );

        pushRoute( routePath );
    }

    return {
        triggerTypeAllowed,
        triggerWorkflowAction,
        getTriggerActionPrompt,
    };
};
